/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Button,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import MUIDataTable from "mui-datatables";
import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Delete, Edit } from "@mui/icons-material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { BreadCrumbsContext } from "../../Context/breadCrumbsContext";
import { getData } from "../../Services/getData";
import { deleteData } from "../../Services/deleteData";
import { LocalUrlEnum, UrlEnum } from "../../Utils/Utils";
import { Vocabulary } from "../../Utils/Vocabulary";
import { breadCrumbsEnum } from "../../Utils/Constants";
import theme from "../../Theme/Theme";
import style from "../Devices/Devices.module.scss";
import DeleteQuestionnaireModal from "../Questionnaire/DeleteQuestionnaireModal";
import DeviceRepairModal from "./DeviceRepairModal";

export default function DeviceRepairs() {
  const navigate = useNavigate();
  const location = useLocation();
  const breadCrumbsContext = useContext(BreadCrumbsContext);
  const notifySuccess = (msg: string) => toast.success(msg);
  const notifyError = (msg: string) => toast.error(msg);

  const [modalOpen, setModalOpen] = useState(false);
  const [editedModel, setEditedModel] = useState<any>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [state, setState] = useState<any>({
    repairs: [],
    repairsNumber: 0,
    page: 0,
    perPage: 20,
    search: null,
  });

  const columns = [
    {
      label: Vocabulary.repairName.toUpperCase(),
      name: "repairName",
      options: { sort: false },
    },
    {
      label: Vocabulary.device.toUpperCase(),
      name: "deviceName",
      options: { sort: false },
    },
    {
      label: Vocabulary.spareParts.toUpperCase(),
      name: "spareParts",
      options: {
        sort: false,
        customBodyRenderLite: (dataIndex: number) => {
          const parts = state.repairs[dataIndex]?.spareParts || [];
          return parts.map((p: any) => p.name).join(", ") || "—";
        },
      },
    },
    {
      label: Vocabulary.labourPrice.toUpperCase(),
      name: "labourPrice",
      options: {
        sort: false,
        customBodyRenderLite: (dataIndex: number) =>
          `${state.repairs[dataIndex]?.labourPrice ?? 0} RON`,
      },
    },
    {
      label: Vocabulary.totalRepairPrice.toUpperCase(),
      name: "totalPrice",
      options: {
        sort: false,
        customBodyRenderLite: (dataIndex: number) =>
          `${state.repairs[dataIndex]?.totalPrice ?? 0} RON`,
      },
    },
    {
      label: Vocabulary.options.toUpperCase(),
      name: "options",
      options: {
        setCellHeaderProps: () => ({ align: "center" as any }),
        setCellProps: () => ({ align: "center" as any }),
        filter: false,
        sort: false,
        empty: true,
        customBodyRenderLite: (dataIndex: number) => (
          <div style={{ marginTop: -10, marginBottom: -10 }}>
            <ToggleButtonGroup exclusive>
              <Tooltip title={Vocabulary.edit}>
                <ToggleButton
                  value="edit"
                  onClick={() => {
                    setEditedModel(state.repairs[dataIndex]);
                    setModalOpen(true);
                  }}
                >
                  <Edit />
                </ToggleButton>
              </Tooltip>
              <Tooltip title={Vocabulary.delete}>
                <ToggleButton
                  value="delete"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteId(state.repairs[dataIndex]._id);
                    setOpenDeleteModal(true);
                  }}
                >
                  <Delete />
                </ToggleButton>
              </Tooltip>
            </ToggleButtonGroup>
          </div>
        ),
      },
    },
  ];

  useEffect(() => {
    breadCrumbsContext.setBreadCrumbs([`${breadCrumbsEnum.deviceRepairs}`]);
    const search = location.search.split("?");
    const newState = { ...state } as any;
    search.forEach((value) => {
      if (value !== "") {
        const [key, val] = value.split("=");
        if (key === "page") newState[key] = +val - 1;
        else newState[key] = isNaN(+val) ? val : +val;
      }
    });
    getRepairs(newState.page, newState.perPage, newState.search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  async function getRepairs(page: number, perPage: number, search?: string | null) {
    setLoading(true);
    let url = `${UrlEnum.deviceRepairs}/${page}/${perPage}`;
    if (search) url += `/${search}`;
    await getData(url)
      .then((res: any) => {
        setState({
          ...state,
          repairs: res.data.repairs,
          repairsNumber: res.data.repairsNumber,
          page,
          perPage,
          search: search || null,
        });
      })
      .catch(() => notifyError(Vocabulary.getError))
      .finally(() => setLoading(false));
  }

  function handleDelete() {
    if (!deleteId) return;
    setLoading(true);
    deleteData(`${UrlEnum.deviceRepairs}/${deleteId}`)
      .then(async () => {
        await getRepairs(state.page, state.perPage, state.search);
        notifySuccess(Vocabulary.deleteSuccess);
      })
      .catch(() => notifyError(Vocabulary.deleteError))
      .finally(() => {
        setLoading(false);
        setOpenDeleteModal(false);
        setDeleteId(null);
      });
  }

  function getTableOptions() {
    return {
      selectableRows: "none" as any,
      viewColumns: false as any,
      responsive: "standard" as any,
      rowsPerPageOptions: [10, 20, 50],
      filter: false,
      print: false,
      download: false,
      textLabels: { body: { noMatch: Vocabulary.noResultsFound } },
      rowsPerPage: state.perPage,
      page: state.page,
      count: state.repairsNumber,
      search: true,
      serverSide: true,
      setRowProps: () => ({ style: { cursor: "pointer" } }),
      onRowClick: (_: any, rowMeta: any) => {
        setEditedModel(state.repairs[rowMeta.rowIndex]);
        setModalOpen(true);
      },
      onChangePage: (page: number) =>
        navigate(`${LocalUrlEnum.deviceRepairs}?page=${page + 1}?perPage=${state.perPage}`),
      onChangeRowsPerPage: (n: number) =>
        navigate(`${LocalUrlEnum.deviceRepairs}?page=1?perPage=${n}`),
      onSearchChange: (search: string | null) => {
        if (search)
          navigate(
            `${LocalUrlEnum.deviceRepairs}?page=${state.page + 1}?perPage=${state.perPage}?search=${search}`
          );
        else
          navigate(
            `${LocalUrlEnum.deviceRepairs}?page=${state.page + 1}?perPage=${state.perPage}`
          );
      },
      onSearchClose: () =>
        navigate(
          `${LocalUrlEnum.deviceRepairs}?page=${state.page + 1}?perPage=${state.perPage}`
        ),
    };
  }

  return (
    <>
      <div className={style.devicesBtn}>
        <Button
          variant="contained"
          size="large"
          style={{
            color: theme.palette.whiteColor?.main,
            backgroundColor: theme.palette.dashboard?.main,
          }}
          onClick={() => {
            setEditedModel(null);
            setModalOpen(true);
          }}
        >
          <AddCircleIcon />
          &nbsp;&nbsp;{Vocabulary.newRepair}
        </Button>
      </div>

      <MUIDataTable
        title={Vocabulary.deviceRepairs}
        data={state.repairs}
        columns={columns}
        options={getTableOptions()}
      />

      {modalOpen && (
        <DeviceRepairModal
          open={modalOpen}
          data={editedModel}
          onClose={async (shouldRefetch) => {
            setModalOpen(false);
            setEditedModel(null);
            if (shouldRefetch) {
              await getRepairs(state.page, state.perPage, state.search);
            }
          }}
        />
      )}

      {openDeleteModal && (
        <DeleteQuestionnaireModal
          openDeleteModal={openDeleteModal}
          setOpenDeleteModal={setOpenDeleteModal}
          handleDelete={handleDelete}
          title={Vocabulary.repairDeleteConfirm}
        />
      )}

      {loading && (
        <div className="loading">
          <CircularProgress color="primary" />
        </div>
      )}
    </>
  );
}
